import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, FileXls, FileCsv } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-MX'); } catch { return iso; }
};

const COLS = [
  { key: 'folio', label: 'Folio', money: false },
  { key: 'started_at', label: 'Fecha', money: false, formatter: fmtDate },
  { key: 'sales_total', label: 'Venta total', money: true },
  { key: 'transfer_total', label: 'Transferencia', money: true },
  { key: 'salario', label: 'Salario', money: true },
  { key: 'pago_envios', label: 'Pago de envío', money: true },
  { key: 'gasolina', label: 'Gasolina', money: true },
  { key: 'misc', label: 'Misceláneos', money: true },
  { key: 'shipping_overflow', label: 'Sobrante envío', money: true },
];

export const CutsReport = () => {
  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setDate(monthAgo.getDate() - 30);
  const [start, setStart] = useState(monthAgo.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const startISO = new Date(start + 'T00:00:00').toISOString();
      const endISO = new Date(end + 'T23:59:59').toISOString();
      const { data } = await axios.get(`${API}/cuts/report/range`, {
        params: { start_date: startISO, end_date: endISO },
        withCredentials: true
      });
      setData(data);
      if (data.rows.length === 0) toast.info('No hay cortes cerrados en ese rango');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!data) return;
    const headers = COLS.map(c => c.label).join(',');
    const rows = data.rows.map(r => COLS.map(c => {
      const v = r[c.key];
      if (c.formatter) return `"${c.formatter(v)}"`;
      if (typeof v === 'number') return v.toFixed(2);
      return `"${String(v ?? '').replace(/"/g, '""')}"`;
    }).join(','));
    const totalRow = ['"TOTAL"', '""', ...COLS.slice(2).map(c => (data.totals[c.key] ?? 0).toFixed(2))].join(',');
    const csv = [headers, ...rows, totalRow].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cortes_${start}_a_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadXLSX = () => {
    if (!data) return;
    // XLSX simple via SpreadsheetML 2003 (XML compatible con Excel)
    const cellsHeader = COLS.map(c => `<Cell><Data ss:Type="String">${c.label}</Data></Cell>`).join('');
    const rowsXml = data.rows.map(r => {
      const cells = COLS.map(c => {
        const v = r[c.key];
        if (c.money) return `<Cell><Data ss:Type="Number">${(Number(v) || 0).toFixed(2)}</Data></Cell>`;
        const text = c.formatter ? c.formatter(v) : (v ?? '');
        return `<Cell><Data ss:Type="String">${String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;')}</Data></Cell>`;
      }).join('');
      return `<Row>${cells}</Row>`;
    }).join('');
    const totalCells = COLS.map((c, i) => {
      if (i === 0) return `<Cell><Data ss:Type="String">TOTAL</Data></Cell>`;
      if (i === 1) return `<Cell><Data ss:Type="String"></Data></Cell>`;
      return `<Cell><Data ss:Type="Number">${(data.totals[c.key] ?? 0).toFixed(2)}</Data></Cell>`;
    }).join('');
    const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="Cortes">
<Table>
<Row>${cellsHeader}</Row>
${rowsXml}
<Row>${totalCells}</Row>
</Table>
</Worksheet>
</Workbook>`;
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cortes_${start}_a_${end}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <div className="container max-w-7xl py-6 space-y-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reporte de cortes</h1>
          <p className="text-muted-foreground">Tabla resumen de ventas y gastos por corte para exportar a Excel.</p>
        </div>

        <Card>
          <CardContent className="py-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
            </div>
            <Button onClick={generate} disabled={loading} data-testid="report-generate" className="bg-violet-600 hover:bg-violet-700">
              {loading ? 'Generando…' : 'Generar tabla'}
            </Button>
            {data && (
              <>
                <Button variant="outline" onClick={downloadXLSX} data-testid="report-xlsx">
                  <FileXls size={18} weight="bold" className="mr-2" /> Excel (.xls)
                </Button>
                <Button variant="outline" onClick={downloadCSV} data-testid="report-csv">
                  <FileCsv size={18} weight="bold" className="mr-2" /> CSV
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {data && (
          <Card>
            <CardHeader>
              <CardTitle>{data.rows.length} cortes en el rango</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-violet-100 sticky top-0">
                  <tr>
                    {COLS.map(c => (
                      <th key={c.key} className={`p-2 ${c.money ? 'text-right' : 'text-left'}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.cut_id} className={`border-t ${i % 2 === 1 ? 'bg-slate-50' : ''}`}>
                      {COLS.map(c => (
                        <td key={c.key} className={`p-2 ${c.money ? 'text-right tabular-nums' : ''}`}>
                          {c.formatter ? c.formatter(r[c.key]) : (c.money ? fmtMoney(r[c.key]) : r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-violet-600 font-bold bg-violet-50">
                    <td className="p-2">TOTAL</td>
                    <td className="p-2"></td>
                    {COLS.slice(2).map(c => (
                      <td key={c.key} className="p-2 text-right tabular-nums">{fmtMoney(data.totals[c.key])}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default CutsReport;
